
import Vue from 'vue';
import { mapState } from 'vuex';
import { FeedbackReport } from '@run-diffusion/shared';
import { db } from '@/firebase';
import { ROUTER } from '@/router/constants';
import FeedbackReportRow from './FeedbackReportRow.vue';
import FeedbackReportDetail from './FeedbackReportDetail.vue';
import FeedbackShortcuts from './FeedbackShortcuts.vue';
import { forgetAdmins } from './feedbackAdmins';
import {
	compareReports,
	FeedbackSort,
	FeedbackView,
	inView,
	KIND_LABELS,
	KindFilter,
	LIVE_STATUSES,
	matchesKind,
	matchesReporter,
	matchesSearch,
	ReporterFilter,
} from './feedbackReports';

// Every report outside Closed is loaded (open ones, and fixes still shipping), and the newest
// this many of all of them: nothing deletes a closed report, so the closed ones outgrow any list.
const LOAD_LIMIT: number = 500;

/**
 * Admin > Feedback: what testers report with the Feedback button (feedbackReports), live. Most
 * come from a Boards V2 board; a developer's agent can also file one or import a list's rows
 * through the feedback API, and the page reads any report the same way.
 *
 * Every report a board sends (submitBoardFeedback), or an agent files, lands here as New. An
 * admin reads it, sets its priority, and approves it, or asks for more, or closes it as won't fix
 * or a duplicate; work starts only on approved reports. A fix waits under Shipping (fixed in a
 * pull request, then merged) until it is deployed. Comments, and a line for each status change,
 * sit under each report for the developers and agents working on it. Nothing is deleted: a closed
 * report stays, under Closed, out of the default view. Only RunDiffusion admins read or change any of it
 * (firestore.rules). The selected report is in the URL (/admin/feedback/12), to share.
 */
export default Vue.extend({
	name: 'AdminFeedback',
	metaInfo: {
		title: 'Feedback | Admin | RunDiffusion',
	},
	data () {
		return {
			// every report outside Closed, and the newest LOAD_LIMIT of all: `reports` is the two together
			openReports: [] as FeedbackReport[],
			newestReports: [] as FeedbackReport[],
			openLoaded: false,
			newestLoaded: false,
			loading: true,
			loadError: false,
			view: 'review' as FeedbackView,
			search: '' as string | null,
			kind: 'all' as KindFilter,
			reporter: 'all' as ReporterFilter,
			sort: 'newest' as FeedbackSort,
			unsubscribe: null as (() => void) | null,
			unsubscribeOpen: null as (() => void) | null,
			// where the selected report last sat in the list: the next one takes its place when it leaves
			lastIndex: 0,
			// a report opened by its number that isn't among the newest LOAD_LIMIT, read on its own
			olderReport: null as FeedbackReport | null,
			olderLoading: false,
			// the number olderUnsubscribe listens for
			olderNumber: null as number | null,
			olderUnsubscribe: null as (() => void) | null,
			loadLimit: LOAD_LIMIT,
			shortcutsOpen: false,
			kindItems: [
				{ text: 'Any kind', value: 'all' },
				...Object.keys(KIND_LABELS).map((value: string) => ({ text: KIND_LABELS[value], value })),
			],
			reporterItems: [
				{ text: 'Anyone', value: 'all' },
				{ text: 'Admins', value: 'admin' },
				{ text: 'Users', value: 'user' },
			],
			sortItems: [
				{ text: 'Newest', value: 'newest' },
				{ text: 'Oldest', value: 'oldest' },
				{ text: 'Priority', value: 'priority' },
			],
		};
	},
	computed: {
		...mapState([
			'user',
		]),
		// Everything but the view: the tabs count what these leave.
		filtered (): FeedbackReport[] {
			return this.reports.filter((report: FeedbackReport) => (
				matchesKind(report, this.kind)
				&& matchesReporter(report, this.reporter)
				&& matchesSearch(report, this.search || '')
			));
		},
		visible (): FeedbackReport[] {
			return this.filtered
				.filter((report: FeedbackReport) => inView(report, this.view))
				.sort(compareReports(this.sort));
		},
		tabs (): Array<{ value: FeedbackView, label: string, count: number }> {
			const count = (view: FeedbackView): number => this.filtered.filter((report: FeedbackReport) => inView(report, view)).length;
			return [
				{ value: 'review', label: 'To review', count: count('review') },
				{ value: 'approved', label: 'Approved', count: count('approved') },
				{ value: 'shipping', label: 'Shipping', count: count('shipping') },
				{ value: 'closed', label: 'Closed', count: count('closed') },
				{ value: 'all', label: 'All', count: count('all') },
			];
		},
		selectedNumber (): number | null {
			const n: number = Number(this.$route.params.number);
			return Number.isInteger(n) && n > 0 ? n : null;
		},
		selected (): FeedbackReport | null {
			if (!this.selectedNumber) return null;
			const loaded: FeedbackReport | undefined = this.reports.find((report: FeedbackReport) => report.number === this.selectedNumber);
			if (loaded) return loaded;
			return this.olderReport && this.olderReport.number === this.selectedNumber ? this.olderReport : null;
		},
		// Every report outside Closed, and the newest LOAD_LIMIT of all, newest first. One in both lists comes
		// from the newest, which carries the same write as the other.
		reports (): FeedbackReport[] {
			const byId: Map<string, FeedbackReport> = new Map();
			this.openReports.forEach((report: FeedbackReport) => byId.set(report.id, report));
			this.newestReports.forEach((report: FeedbackReport) => byId.set(report.id, report));
			return Array.from(byId.values()).sort((a: FeedbackReport, b: FeedbackReport) => b.number - a.number);
		},
		// closed reports older than the newest LOAD_LIMIT aren't in the list
		truncated (): boolean {
			return this.newestReports.length >= LOAD_LIMIT;
		},
		// "12" or "#12" in the search box
		searchNumber (): number | null {
			const match: RegExpMatchArray | null = (this.search || '').trim().match(/^#?(\d+)$/);
			return match ? Number(match[1]) : null;
		},
		// null when the selected report isn't in this tab's list
		selectedIndex (): number | null {
			if (!this.selected) return null;
			const i: number = this.visible.findIndex((report: FeedbackReport) => report.id === this.selected.id);
			return i < 0 ? null : i;
		},
		// a phone showing one report: the header and filters step aside for it
		focusedOnReport (): boolean {
			return this.$vuetify.breakpoint.smAndDown && !!this.selected;
		},
		filtersActive (): boolean {
			return this.kind !== 'all' || this.reporter !== 'all' || !!(this.search || '').trim();
		},
		// The tab holding the report whose number is searched, when it isn't this one
		searchElsewhere (): { value: FeedbackView, label: string } | null {
			const n: number | null = this.searchNumber;
			if (!n || this.visible.length) return null;
			const found: FeedbackReport | undefined = this.reports.find((report: FeedbackReport) => report.number === n);
			if (!found || inView(found, this.view)) return null;
			const tab = this.tabs.find((t: { value: FeedbackView }) => t.value !== 'all' && inView(found, t.value));
			return tab ? { value: tab.value, label: tab.label } : null;
		},
		empty (): { icon: string, title: string, detail: string } {
			if (!this.reports.length) return { icon: 'mdi-message-text-outline', title: 'No feedback yet', detail: 'Reports land here the moment someone sends one.' };
			if (this.searchElsewhere) return { icon: 'mdi-folder-search-outline', title: `#${this.searchNumber} is in ${this.searchElsewhere.label}`, detail: 'This tab doesn\'t hold it.' };
			if (this.filtersActive) return { icon: 'mdi-filter-variant-remove', title: 'Nothing matches', detail: 'No report in this tab matches these filters.' };
			if (this.view === 'review') return { icon: 'mdi-check-all', title: 'All caught up', detail: 'Nothing is waiting on review.' };
			if (this.view === 'approved') return { icon: 'mdi-hammer-wrench', title: 'Nothing approved', detail: 'Approved reports wait here until they are fixed.' };
			if (this.view === 'shipping') return { icon: 'mdi-source-pull', title: 'Nothing shipping', detail: 'Fixed and merged reports wait here until they are deployed.' };
			return { icon: 'mdi-archive-outline', title: 'Nothing closed yet', detail: 'Deployed reports, and those closed as won\'t fix or duplicate, are kept here.' };
		},
		viewLabel (): string {
			const tab = this.tabs.find((t: { value: FeedbackView }) => t.value === this.view);
			return tab ? tab.label : '';
		},
	},
	watch: {
		// A report opened by its number that the list doesn't hold (it's older than the newest
		// LOAD_LIMIT) is read on its own.
		selectedNumber: {
			handler () {
				this.watchOlderReport();
			},
		},
		loading () {
			this.watchOlderReport();
		},
		// The open report can drop out of the newest LOAD_LIMIT when new ones arrive: it carries on
		// as an older report, seeded from the list it left so the pane (and any draft in it) stays.
		reports (now: FeedbackReport[], before: FeedbackReport[]) {
			const n: number | null = this.selectedNumber;
			this.watchOlderReport(n ? before.find((report: FeedbackReport) => report.number === n) : undefined);
		},
		selectedIndex (i: number | null) {
			if (i !== null) this.lastIndex = i;
		},
		// a new report opens at its top
		selected (now: FeedbackReport | null, before: FeedbackReport | null) {
			if (now && before && now.id === before.id) return;
			const pane = this.$refs.detailPane as Vue | undefined;
			if (pane && pane.$el) pane.$el.scrollTop = 0;
		},
	},
	mounted () {
		window.addEventListener('keydown', this.onKey);
		const failed = (e: Error) => {
			console.error('[feedback] reports failed to load', e);
			this.loading = false;
			this.loadError = true;
		};
		this.unsubscribe = db.collection('feedbackReports')
			.orderBy('number', 'desc')
			.limit(LOAD_LIMIT)
			.onSnapshot((snap) => {
				this.newestReports = snap.docs.map((doc) => ({ ...(doc.data() as FeedbackReport), id: doc.id }));
				this.newestLoaded = true;
				this.loading = !this.openLoaded;
			}, failed);
		// by status alone, so no composite index: the list sorts them
		this.unsubscribeOpen = db.collection('feedbackReports')
			.where('status', 'in', LIVE_STATUSES)
			.onSnapshot((snap) => {
				this.openReports = snap.docs.map((doc) => ({ ...(doc.data() as FeedbackReport), id: doc.id }));
				this.openLoaded = true;
				this.loading = !this.newestLoaded;
			}, failed);
	},
	beforeDestroy () {
		window.removeEventListener('keydown', this.onKey);
		if (this.unsubscribe) this.unsubscribe();
		if (this.unsubscribeOpen) this.unsubscribeOpen();
		this.unsubscribe = null;
		this.unsubscribeOpen = null;
		this.stopOlderReport();
		// the Assign picker's admins are read once a visit: the next visit reads them again
		forgetAdmins();
	},
	methods: {
		goBack () {
			this.routerPush(this.$route, this.$router, { name: ROUTER.SESSIONS });
		},
		select (report: FeedbackReport) {
			this.routerPush(this.$route, this.$router, { name: ROUTER.ADMIN_FEEDBACK, params: { number: String(report.number) } });
		},
		// Esc in the search box: back to the shortcuts
		leaveSearch () {
			const search = this.$refs.search as Vue | undefined;
			const input: HTMLElement | null = search ? search.$el.querySelector('input') : null;
			if (input) input.blur();
		},
		stopOlderReport () {
			if (this.olderUnsubscribe) this.olderUnsubscribe();
			this.olderUnsubscribe = null;
			this.olderNumber = null;
			this.olderLoading = false;
		},
		// `seed` shows the report until its own listener answers
		watchOlderReport (seed?: FeedbackReport) {
			const n: number | null = this.selectedNumber;
			if (this.loading || !n || this.reports.some((report: FeedbackReport) => report.number === n)) {
				this.stopOlderReport();
				this.olderReport = null;
				return;
			}
			if (this.olderUnsubscribe && this.olderNumber === n) return;
			this.stopOlderReport();
			this.olderReport = seed || null;
			this.olderLoading = !seed;
			this.olderNumber = n;
			// With metadata changes, so the server's answer arrives even when it only confirms the cache's
			this.olderUnsubscribe = db.collection('feedbackReports').where('number', '==', n).limit(1)
				.onSnapshot({ includeMetadataChanges: true }, (snap) => {
					// Offline, the first answer can be an empty one from the local cache: it doesn't
					// mean the report is gone, so the seed (and the pane, drafts and all) stays
					if (snap.empty && snap.metadata.fromCache) return;
					this.olderReport = snap.empty ? null : { ...(snap.docs[0].data() as FeedbackReport), id: snap.docs[0].id };
					this.olderLoading = false;
				}, (e: Error) => {
					console.error('[feedback] report', n, 'failed to load', e);
					this.olderLoading = false;
				});
		},
		openNumber (n: number) {
			this.routerPush(this.$route, this.$router, { name: ROUTER.ADMIN_FEEDBACK, params: { number: String(n) } });
		},
		clearFilters () {
			this.kind = 'all';
			this.reporter = 'all';
			this.search = '';
		},
		clearSelection () {
			this.routerPush(this.$route, this.$router, { name: ROUTER.ADMIN_FEEDBACK });
		},
		// The report `by` places down (or up) the list; with none selected, the first.
		move (by: number) {
			if (!this.visible.length) return;
			const from: number = this.selectedIndex === null ? (by > 0 ? -1 : this.visible.length) : this.selectedIndex;
			const next: FeedbackReport | undefined = this.visible[Math.min(this.visible.length - 1, Math.max(0, from + by))];
			if (!next || (this.selected && next.id === this.selected.id)) return;
			this.select(next);
			this.$nextTick(() => {
				const row: Element | null = this.$el.querySelector(`[data-test="report-${next.number}"]`);
				if (row) row.scrollIntoView({ block: 'nearest' });
			});
		},
		// A report that left this tab (approved out of To review, say) hands over to the one after it.
		onStatusChanged () {
			if (!this.selected || this.selectedIndex !== null || !this.visible.length) return;
			this.select(this.visible[Math.min(this.lastIndex, this.visible.length - 1)]);
		},
		onKey (e: KeyboardEvent) {
			if (e.metaKey || e.ctrlKey || e.altKey || e.defaultPrevented) return;
			// a held key repeats: fine for moving through the list, never for acting on a report
			if (e.repeat && e.key !== 'j' && e.key !== 'k') return;
			const target: HTMLElement | null = e.target as HTMLElement | null;
			if (target && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName))) return;
			// a dialog (the screenshot viewer, a status change) has the keys while it's open
			if (document.querySelector('.v-dialog--active')) return;
			const detail = this.$refs.detail as (Vue & { approve: () => boolean, focusComment: () => void }) | undefined;
			// hidden on a narrow screen while a report is open
			const search = this.$refs.search as (Vue & { focus: () => void }) | undefined;
			if (e.key === 'j') this.move(1);
			else if (e.key === 'k') this.move(-1);
			else if (e.key === '?') this.shortcutsOpen = true;
			else if (e.key === '/' && search) search.focus();
			else if (e.key === 'a' && detail) detail.approve();
			else if (e.key === 'c' && detail) detail.focusComment();
			else return;
			e.preventDefault();
		},
	},
	components: {
		FeedbackReportDetail,
		FeedbackReportRow,
		FeedbackShortcuts,
	},
});
